import React from 'react';
import { useTranslation } from 'react-i18next';
import { Crosshair, FileText, RefreshCw } from 'lucide-react';

export function IntangiHubOrders({ orders, loading }) {
  const { t } = useTranslation();
  if (loading) {
    return (
      <div className="bg-[#090B0F] border border-white/10 p-4" data-testid="intangihub-orders">
        <div className="flex items-center justify-center py-8">
          <RefreshCw className="w-5 h-5 text-purple-400 animate-spin" />
        </div>
      </div>
    );
  }

  const activeOrders = orders?.orders || [];
  const executedOrders = orders?.recent_executed || [];
  const executedCount = orders?.executed_count || 0;

  return (
    <div className="bg-[#090B0F] border border-white/10 p-4" data-testid="intangihub-orders">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Crosshair className="w-5 h-5 text-purple-400" />
          <h3 className="text-sm uppercase tracking-[0.2em] font-semibold text-white">{t('dashboard.intangiOrders')}</h3>
          <span className="text-[10px] font-mono text-purple-400 bg-purple-400/10 px-2 py-0.5">AGT-15</span>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-[10px] font-mono text-slate-500">{executedCount} ejecutadas</span>
          <span className="text-[10px] font-mono text-slate-500">{activeOrders.length} activas</span>
        </div>
      </div>

      {[...executedOrders, ...activeOrders.slice(0, 10)].length === 0 ? (
        <div className="text-center py-6">
          <FileText className="w-8 h-8 text-slate-600 mx-auto mb-2" />
          <p className="text-slate-500 text-xs font-mono">{t('dashboard.noOrdersYet')}</p>
          <p className="text-slate-600 text-[10px] font-mono mt-1">{t('dashboard.noOrdersDesc')}</p>
        </div>
      ) : (
        <div className="space-y-2 max-h-[400px] overflow-y-auto custom-scrollbar">
          {executedOrders.map((order, idx) => {
            const isBracket = !!order.bracket_group_id;
            const isOCO = !!order.oco_group_id;
            const orderId = order.bracket_group_id || order.oco_group_id || order.order_id || `exec-${idx}`;
            const entryPrice = order.entry?.price || order.price || 0;
            const slPrice = order.stop_loss?.price || 0;
            const tpPrice = order.take_profit?.price || 0;
            const status = order.entry?.status || order.status || 'open';
            const side = entryPrice > 0 && tpPrice > entryPrice ? 'BUY' : tpPrice > 0 && tpPrice < entryPrice ? 'SELL' : 'BUY';
            const createdAt = order.created_at ? new Date(order.created_at).toLocaleString('es-ES', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }) : '';

            return (
              <div key={orderId} className="bg-[#11141D] border border-white/5 p-3 hover:border-purple-500/30 transition-all" data-testid={`intangi-order-${orderId}`}>
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <span className={`text-[10px] font-mono font-bold px-1.5 py-0.5 ${side === 'BUY' ? 'text-emerald-400 bg-emerald-400/10' : 'text-red-400 bg-red-400/10'}`}>{side}</span>
                    <span className="text-[10px] font-mono text-purple-400">{isBracket ? 'BRACKET' : isOCO ? 'OCO' : 'LIMIT'}</span>
                    <span className="text-[10px] font-mono text-slate-500">{orderId.slice(0, 12)}</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <div className={`w-1.5 h-1.5 rounded-full ${status === 'open' ? 'bg-amber-400 animate-pulse' : status === 'filled' ? 'bg-emerald-400' : 'bg-slate-500'}`} />
                    <span className="text-[10px] font-mono text-slate-400">{status.toUpperCase()}</span>
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-2">
                  <div>
                    <p className="text-[10px] text-slate-500">{t('dashboard.entry')}</p>
                    <p className="font-mono text-xs text-white">${entryPrice.toLocaleString(undefined, { maximumFractionDigits: 4 })}</p>
                  </div>
                  {slPrice > 0 && (
                    <div>
                      <p className="text-[10px] text-red-400/70">{t('dashboard.stopLoss')}</p>
                      <p className="font-mono text-xs text-red-400">${slPrice.toLocaleString(undefined, { maximumFractionDigits: 4 })}</p>
                    </div>
                  )}
                  {tpPrice > 0 && (
                    <div>
                      <p className="text-[10px] text-emerald-400/70">{t('dashboard.takeProfit')}</p>
                      <p className="font-mono text-xs text-emerald-400">${tpPrice.toLocaleString(undefined, { maximumFractionDigits: 4 })}</p>
                    </div>
                  )}
                </div>
                {createdAt && <p className="text-[10px] font-mono text-slate-600 mt-1.5">{createdAt}</p>}
              </div>
            );
          })}

          {activeOrders.slice(0, 10).map((order, idx) => {
            const orderId = order.order_id || `active-${idx}`;
            const price = order.price || 0;
            const side = order.side || 'buy';
            const qty = order.quantity || order.remaining_quantity || 0;
            const orderType = order.order_type || order.type || 'limit';
            const symbol = order.symbol || 'IPIX/USD';
            const status = order.status || 'open';

            return (
              <div key={orderId} className="bg-[#0D1017] border border-white/5 p-3 hover:border-white/10 transition-all" data-testid={`intangi-active-order-${orderId}`}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className={`text-[10px] font-mono font-bold px-1.5 py-0.5 ${side === 'buy' ? 'text-emerald-400 bg-emerald-400/10' : 'text-red-400 bg-red-400/10'}`}>{side.toUpperCase()}</span>
                    <span className="text-[10px] font-mono text-slate-400">{orderType.toUpperCase()}</span>
                    <span className="font-mono text-xs text-white">{symbol}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="font-mono text-xs text-white">${price.toFixed(4)}</span>
                    <span className="text-[10px] font-mono text-slate-500">x{qty}</span>
                    <div className={`w-1.5 h-1.5 rounded-full ${status === 'open' ? 'bg-amber-400 animate-pulse' : 'bg-slate-500'}`} />
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
